import * as React from 'react';

import { SimpleTreeView } from '@mui/x-tree-view';
import { Box, Skeleton } from '@mui/material';

import { Product } from '../../types/Product';
import { WorkshopProduct } from '../../types/WorkshopProducts';
import { ProductInterface } from '../../types/ProductInterface';
import { TestCombination } from '../../types/TestCombinations';
import { WorkshopProductCombination, WSProdConfigCounters } from '../../types/WorkshopProductCombination';

import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { FolderLabel } from '../../subs/FolderLabel';
import { TestCase } from '../../types/TestCase';
import { WorkshopTestCaseMap } from '../../types/WorkshopTestCases';
import { StyledTreeItem } from '../../subs/StyledTreeItem';

const GetEmptyConfigCounters = () => {
    let counters: WSProdConfigCounters = {
        testCaseSum: 0,
        testCaseMarked: 0,
        workshopProductCombinationId: 0
    }
    return counters;
}

const ProductTreeItem = ({ treeType, product, workshopProduct, wsProdCombination, wstcCounter }:
    {
        product: Product, treeType: string, 
        workshopProduct: WorkshopProduct,
        wsProdCombination: WorkshopProductCombination,
        wstcCounter: WSProdConfigCounters
    }) => {

    return <>
        {workshopProduct.id && (<StyledTreeItem itemId={treeType + '-' + workshopProduct.id.toString()}
            label={<FolderLabel labelText={`${product.name} ${workshopProduct.productVersion ?? ''}`}
                alternateIcon={wsProdCombination.consumerReadyToTest && wsProdCombination.isConsumer ? 0 :
                    wsProdCombination.providerReadyToTest && wsProdCombination.isProvider ? 0 : 1}
                labelVariant='small' color={wstcCounter.testCaseSum === wstcCounter.testCaseMarked ? 'success' : 'info'}
                passed={wstcCounter.testCaseMarked} info={wstcCounter.testCaseSum - wstcCounter.testCaseMarked}
            />}
        />)}
    </>
}

const TestCombinationTree = ({ products, workshopProducts, wsProdCombinations, testCombination,
    wsProdConfigCounters, wstcCounter, wstcCounterC, wstcCounterP }:
    {
        products: Product[], workshopProducts: WorkshopProduct[],
        wsProdCombinations: WorkshopProductCombination[], testCombination: TestCombination,
        wsProdConfigCounters: WSProdConfigCounters[],
        wstcCounter: WSProdConfigCounters,
        wstcCounterC: WSProdConfigCounters,
        wstcCounterP: WSProdConfigCounters
    }) => {

    return <>
        {testCombination.id !== undefined && wsProdCombinations.find(wspc => wspc.testCombinationId === testCombination.id) !== undefined &&
            <StyledTreeItem itemId={testCombination.id.toString()} label={<FolderLabel labelText={testCombination.combinationName ?? 'error'} labelVariant='large'
                color={wstcCounter.testCaseSum === wstcCounter.testCaseMarked ? 'success' : 'info'}
                passed={wstcCounter.testCaseMarked} info={wstcCounter.testCaseSum - wstcCounter.testCaseMarked}
            />}>
                <StyledTreeItem itemId={testCombination.id.toString() + '-C'} label={<FolderLabel labelText={testCombination.combinationConsumer ?? 'error'} labelVariant='large'
                    color={wstcCounterC.testCaseSum === wstcCounterC.testCaseMarked ? 'success' : 'info'}
                    passed={wstcCounterC.testCaseMarked} info={wstcCounterC.testCaseSum - wstcCounterC.testCaseMarked}
                />}>
                    {wsProdCombinations.map(wspc =>
                        wspc.testCombinationId === testCombination.id && wspc.workshopProductId !== undefined && wspc.isConsumer &&
                        <ProductTreeItem treeType={testCombination.id.toString() + '-C'}
                            product={products.find(p => workshopProducts.find(wp => wp.id === wspc.workshopProductId)?.productId === p.id) as Product}
                            wsProdCombination={wspc}
                            workshopProduct={workshopProducts.find(wp => wp.id === wspc.workshopProductId) as WorkshopProduct}
                            wstcCounter={wsProdConfigCounters.find(wpcc => wpcc.workshopProductCombinationId === wspc.id) ?? GetEmptyConfigCounters()}
                        />
                    )}
                </StyledTreeItem>
                <StyledTreeItem itemId={testCombination.id.toString() + '-P'} label={<FolderLabel labelText={testCombination.combinationProvider ?? 'error'} labelVariant='large'
                    color={wstcCounterP.testCaseSum === wstcCounterP.testCaseMarked ? 'success' : 'info'}
                    passed={wstcCounterP.testCaseMarked} info={wstcCounterP.testCaseSum - wstcCounterP.testCaseMarked}
                />}>
                    {wsProdCombinations.map(wspc =>
                        wspc.testCombinationId === testCombination.id && wspc.workshopProductId !== undefined && wspc.isProvider &&
                        <ProductTreeItem treeType={testCombination.id.toString() + '-P'}
                            product={products.find(p => workshopProducts.find(wp => wp.id === wspc.workshopProductId)?.productId === p.id) as Product}
                            wsProdCombination={wspc}
                            workshopProduct={workshopProducts.find(wp => wp.id === wspc.workshopProductId) as WorkshopProduct}
                            wstcCounter={wsProdConfigCounters.find(wpcc => wpcc.workshopProductCombinationId === wspc.id) ?? GetEmptyConfigCounters()}
                        />
                    )}
                </StyledTreeItem>
            </StyledTreeItem>
        }
    </>
}

export const ProductTreeView = ({ products, workshopProducts, wsProdCombinations, testCombinations,
    getSelectedNode, nodeSelectHandler, wsProdConfigCounters }:
    {
        products: Product[], workshopProducts: WorkshopProduct[],
        wsProdCombinations: WorkshopProductCombination[], testCombinations: TestCombination[],
        wsProdConfigCounters: WSProdConfigCounters[],
        getSelectedNode: () => string,
        nodeSelectHandler: (event: React.SyntheticEvent, itemId: string) => void
    }) => {
    const [wstcCounters, setWstcCounters] = React.useState<Map<string, WSProdConfigCounters>>(new Map<string, WSProdConfigCounters>());

    React.useEffect(() => {
        let wstcCounterMap: Map<string, WSProdConfigCounters> = new Map<string, WSProdConfigCounters>();
        let wstcCounter: WSProdConfigCounters = GetEmptyConfigCounters();
        let wstcCounterC: WSProdConfigCounters = GetEmptyConfigCounters();
        let wstcCounterP: WSProdConfigCounters = GetEmptyConfigCounters();

        testCombinations.forEach(combo => {
            if (combo.id === undefined) return;
            wstcCounter = GetEmptyConfigCounters();
            wstcCounterC = GetEmptyConfigCounters();
            wstcCounterP = GetEmptyConfigCounters();

            wsProdCombinations.forEach(wspc => {
                if (wspc.testCombinationId !== combo.id) return;
                if (wspc.workshopProductId === undefined) return;
                let wsProdConfigCounter = wsProdConfigCounters.find(wpcc => wpcc.workshopProductCombinationId === wspc.id);
                if (wsProdConfigCounter) {
                    wstcCounter.testCaseSum += wsProdConfigCounter.testCaseSum;
                    wstcCounter.testCaseMarked += wsProdConfigCounter.testCaseMarked;
                    wstcCounter.workshopProductCombinationId = 0;
                }
                if (wspc.isConsumer) {
                    wstcCounterC.testCaseSum += wsProdConfigCounter?.testCaseSum ?? 0;
                    wstcCounterC.testCaseMarked += wsProdConfigCounter?.testCaseMarked ?? 0;
                    wstcCounterC.workshopProductCombinationId = 0;
                }
                if (wspc.isProvider) {
                    wstcCounterP.testCaseSum += wsProdConfigCounter?.testCaseSum ?? 0;
                    wstcCounterP.testCaseMarked += wsProdConfigCounter?.testCaseMarked ?? 0;
                    wstcCounterP.workshopProductCombinationId = 0;
                }
            })
            wstcCounterMap.set(combo.id.toString(), wstcCounter);
            wstcCounterMap.set(combo.id.toString() + combo.combinationConsumer, wstcCounterC);
            wstcCounterMap.set(combo.id.toString() + combo.combinationProvider, wstcCounterP);
        })
        setWstcCounters(wstcCounterMap);
    }, [testCombinations, wsProdCombinations, wsProdConfigCounters]);

    return <Box minWidth={'35%'}>
        {testCombinations ? (
            <SimpleTreeView slots={{ collapseIcon: RemoveIcon, expandIcon: AddIcon }} selectedItems={getSelectedNode()} onItemSelectionToggle={nodeSelectHandler}>
                {testCombinations?.map(combo =>
                    combo.id !== undefined && wsProdCombinations.find(wspc => wspc.testCombinationId === combo.id) !== undefined &&
                    <TestCombinationTree products={products} workshopProducts={workshopProducts} wsProdCombinations={wsProdCombinations}
                        testCombination={combo} wsProdConfigCounters={wsProdConfigCounters}
                        wstcCounter={wstcCounters.get(combo.id.toString()) ?? GetEmptyConfigCounters()}
                        wstcCounterC={wstcCounters.get(combo.id.toString() + combo.combinationConsumer) ?? GetEmptyConfigCounters()}
                        wstcCounterP={wstcCounters.get(combo.id.toString() + combo.combinationProvider) ?? GetEmptyConfigCounters()} 
                    />
                )}
            </SimpleTreeView>
        ) : (
            <Skeleton variant="rectangular" width={'90%'} height={118} />
        )}
    </Box>
}